// A small centered status banner for connection/loading states.

import { injectTheme } from "./theme.js";

const STYLE_ID = "ghostland-status-style";
const OVERLAY_ID = "gl-status-overlay";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#${OVERLAY_ID} {
  position: fixed; inset: 0; z-index: 20;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0, 0, 0, 0.82); color: #f4f4f4;
}
/* this overlay is intentionally PIXEL-styled (overrides the global sans face) */
#${OVERLAY_ID}, #${OVERLAY_ID} * { font-family: 'Silkscreen', ui-monospace, monospace !important; }

#${OVERLAY_ID} .gl-status-card {
  position: relative;
  background: #0b0b0b; border: 3px solid #f4f4f4; border-radius: 0;
  padding: 26px 32px; text-align: center;
  box-shadow:
    inset 0 0 0 3px #000,
    inset 0 0 0 6px #2c2c2c,
    0 0 0 3px #000,
    8px 8px 0 0 rgba(0,0,0,.6);
}
.gl-status-corner { position: absolute; width: 9px; height: 9px; background: #f4f4f4; box-shadow: 0 0 0 3px #000; }
.gl-status-corner.tl { top: -6px; left: -6px; }
.gl-status-corner.tr { top: -6px; right: -6px; }
.gl-status-corner.bl { bottom: -6px; left: -6px; }
.gl-status-corner.br { bottom: -6px; right: -6px; }

#${OVERLAY_ID} .gl-status-msg { font-size: 13px; letter-spacing: 0.12em; text-transform: uppercase; color: #f4f4f4; }
/* a pixel-block loader: three squares blinking in sequence (hard steps, no easing) */
#${OVERLAY_ID} .gl-status-dots { display: flex; gap: 6px; justify-content: center; margin: 0 auto 16px; }
#${OVERLAY_ID} .gl-status-dots span {
  width: 10px; height: 10px; background: #f4f4f4; image-rendering: pixelated;
  animation: gl-status-blink 0.9s steps(1, end) infinite;
}
#${OVERLAY_ID} .gl-status-dots span:nth-child(2) { animation-delay: 0.15s; }
#${OVERLAY_ID} .gl-status-dots span:nth-child(3) { animation-delay: 0.3s; }
@keyframes gl-status-blink { 0%, 100% { opacity: 0.18; } 40% { opacity: 1; } }

#${OVERLAY_ID} button {
  margin-top: 16px; padding: 12px 18px; cursor: pointer;
  background: #f4f4f4; border: 3px solid #fff; border-radius: 0; color: #000;
  font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; box-shadow: 4px 4px 0 0 #000;
}
#${OVERLAY_ID} button:hover { background: #fff; }
#${OVERLAY_ID} button:active { transform: translate(2px, 2px); box-shadow: 2px 2px 0 0 #000; }
`;

function ensureStyle(): void {
  injectTheme();
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

interface StatusOptions {
  spinner?: boolean;
  actionLabel?: string;
  onAction?: () => void;
}

export function showStatus(message: string, options: StatusOptions = {}): void {
  ensureStyle();
  hideStatus();

  const overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;

  const card = document.createElement("div");
  card.className = "gl-status-card";
  for (const pos of ["tl", "tr", "bl", "br"]) {
    const corner = document.createElement("span");
    corner.className = `gl-status-corner ${pos}`;
    card.appendChild(corner);
  }
  if (options.spinner) {
    const dots = document.createElement("div");
    dots.className = "gl-status-dots";
    dots.append(...Array.from({ length: 3 }, () => document.createElement("span")));
    card.appendChild(dots);
  }
  const msg = document.createElement("div");
  msg.className = "gl-status-msg";
  msg.textContent = message;
  card.appendChild(msg);

  if (options.actionLabel) {
    const button = document.createElement("button");
    button.textContent = options.actionLabel;
    button.addEventListener("click", () => options.onAction?.());
    card.appendChild(button);
  }

  overlay.appendChild(card);
  document.body.appendChild(overlay);
}

export function hideStatus(): void {
  document.getElementById(OVERLAY_ID)?.remove();
}
